import React from 'react';
import { motion } from 'framer-motion';
import { 
  Trophy, Star, Calendar, Gift, TrendingUp, Heart, 
  Clock, Zap, Award, Crown, Flame, Target, 
  Moon, Sun, Coffee, PartyPopper, Users, Sparkles
} from 'lucide-react';

// 🏅 DÉFINITION DES BADGES
export const BADGES = {
  // Badges de fidélité
  firstStamp: {
    id: 'firstStamp',
    name: 'Premier Pas',
    description: 'Premier tampon obtenu',
    icon: Star,
    color: '#fbbf24',
    requirement: (stats) => stats.totalStamps >= 1
  },
  collector10: {
    id: 'collector10',
    name: 'Collectionneur',
    description: '10 tampons collectés',
    icon: Trophy,
    color: '#f97316',
    requirement: (stats) => stats.totalStamps >= 10
  },
  collector50: {
    id: 'collector50',
    name: 'Super Collectionneur',
    description: '50 tampons collectés',
    icon: Crown,
    color: '#a855f7',
    requirement: (stats) => stats.totalStamps >= 50
  },
  collector100: {
    id: 'collector100',
    name: 'Légende',
    description: '100 tampons collectés',
    icon: Sparkles,
    color: '#ec4899',
    requirement: (stats) => stats.totalStamps >= 100
  },
  
  // Badges de temps
  earlyBird: {
    id: 'earlyBird',
    name: 'Lève-tôt',
    description: 'Connexion avant 8h',
    icon: Sun,
    color: '#fbbf24',
    requirement: (stats) => stats.earlyConnections >= 1
  },
  nightOwl: {
    id: 'nightOwl',
    name: 'Noctambule',
    description: 'Connexion après 23h',
    icon: Moon,
    color: '#6366f1',
    requirement: (stats) => stats.lateConnections >= 1
  },
  weekendWarrior: {
    id: 'weekendWarrior',
    name: 'Week-end Warrior',
    description: '5 visites le week-end',
    icon: PartyPopper,
    color: '#ec4899',
    requirement: (stats) => stats.weekendVisits >= 5
  },
  
  // Badges de streak
  streak7: {
    id: 'streak7',
    name: 'Fidèle',
    description: '7 jours de suite',
    icon: Flame,
    color: '#f97316',
    requirement: (stats) => stats.maxStreak >= 7
  },
  streak30: {
    id: 'streak30',
    name: 'Inconditionnel',
    description: '30 jours de suite',
    icon: Zap,
    color: '#eab308',
    requirement: (stats) => stats.maxStreak >= 30
  },
  
  // Badges sociaux
  referrer1: {
    id: 'referrer1',
    name: 'Ambassadeur',
    description: '1 filleul parrainé',
    icon: Users,
    color: '#06b6d4',
    requirement: (stats) => stats.referralCount >= 1
  },
  referrer5: {
    id: 'referrer5',
    name: 'Parrain en Or',
    description: '5 filleuls parrainés',
    icon: Award,
    color: '#fbbf24',
    requirement: (stats) => stats.referralCount >= 5
  },
  referrer10: {
    id: 'referrer10',
    name: 'Influenceur',
    description: '10 filleuls parrainés',
    icon: Crown,
    color: '#a855f7',
    requirement: (stats) => stats.referralCount >= 10
  },
  
  // Badges de jeux
  gamer: {
    id: 'gamer',
    name: 'Gamer',
    description: '10 parties jouées',
    icon: Target,
    color: '#22c55e',
    requirement: (stats) => stats.gamesPlayed >= 10
  },
  luckyWinner: {
    id: 'luckyWinner',
    name: 'Chanceux',
    description: 'Gagné au jeu',
    icon: Gift,
    color: '#ec4899',
    requirement: (stats) => stats.gamesWon >= 1
  },
  
  // Badges spéciaux
  veteran: {
    id: 'veteran',
    name: 'Vétéran',
    description: 'Client depuis 6 mois',
    icon: Heart,
    color: '#ef4444',
    requirement: (stats) => stats.daysSinceJoin >= 180
  },
  og: {
    id: 'og',
    name: 'OG',
    description: 'Client depuis 1 an',
    icon: Crown,
    color: '#fbbf24',
    requirement: (stats) => stats.daysSinceJoin >= 365
  },
  birthday: {
    id: 'birthday',
    name: 'Anniversaire',
    description: 'C\'est ton anniversaire !',
    icon: PartyPopper,
    color: '#ec4899',
    requirement: (stats) => stats.isBirthday
  },
};

/**
 * Calcule les badges débloqués pour un client
 */
export function calculateBadges(customer, cards = []) {
  const stats = calculateStats(customer, cards);
  const unlockedBadges = [];
  
  Object.values(BADGES).forEach(badge => {
    if (badge.requirement(stats)) {
      unlockedBadges.push(badge);
    }
  });
  
  return unlockedBadges;
}

/**
 * Calcule les statistiques du client
 */
export function calculateStats(customer, cards = []) {
  const now = new Date();
  const joinDate = customer?.createdAt ? new Date(customer.createdAt) : now;
  const daysSinceJoin = Math.floor((now - joinDate) / (1000 * 60 * 60 * 24));
  
  // Calculer les tampons totaux
  const totalStamps = cards.reduce((acc, card) => {
    return acc + (card.boxes?.filter(b => b).length || 0);
  }, 0);
  
  // Cartes complétées
  const completedCards = customer?.completedCardsCount || 0;
  
  // Économies estimées (10€ par carte complétée en moyenne)
  const totalSavings = completedCards * 10;
  
  // Parrainage
  const referralCount = customer?.referralCount || 0;
  
  // Vérifier si c'est l'anniversaire
  const isBirthday = customer?.birthDate ? 
    new Date(customer.birthDate).getDate() === now.getDate() &&
    new Date(customer.birthDate).getMonth() === now.getMonth() : false;
  
  // Stats simulées (à connecter au backend plus tard)
  const currentHour = now.getHours();
  const isWeekend = now.getDay() === 0 || now.getDay() === 6;
  
  return {
    daysSinceJoin,
    totalStamps,
    completedCards,
    totalSavings,
    referralCount,
    isBirthday,
    // Stats qui nécessiteraient un tracking backend
    earlyConnections: currentHour < 8 ? 1 : 0,
    lateConnections: currentHour >= 23 ? 1 : 0,
    weekendVisits: isWeekend ? 1 : 0,
    maxStreak: customer?.loginStreak || 1,
    gamesPlayed: customer?.gamesPlayed || 0,
    gamesWon: customer?.gamesWon || 0,
  };
}

/**
 * Composant Badge individuel
 */
export function Badge({ badge, unlocked = true, size = 'md' }) {
  const Icon = badge.icon;
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-14 h-14',
    lg: 'w-20 h-20'
  };
  const iconSizes = {
    sm: 'w-5 h-5',
    md: 'w-7 h-7',
    lg: 'w-10 h-10'
  };

  return (
    <motion.div
      className="flex flex-col items-center gap-1"
      whileHover={{ scale: 1.1 }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div
        className={`${sizeClasses[size]} rounded-full flex items-center justify-center relative ${
          unlocked ? '' : 'grayscale opacity-40'
        }`}
        style={{
          background: unlocked 
            ? `linear-gradient(135deg, ${badge.color}40 0%, ${badge.color}20 100%)`
            : '#374151',
          border: `2px solid ${unlocked ? badge.color : '#4b5563'}`,
          boxShadow: unlocked ? `0 0 15px ${badge.color}40` : 'none'
        }}
      >
        <Icon 
          className={iconSizes[size]} 
          style={{ color: unlocked ? badge.color : '#6b7280' }}
        />
        {unlocked && (
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ border: `2px solid ${badge.color}` }}
            animate={{ scale: [1, 1.2, 1], opacity: [1, 0, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
        )}
      </div>
      <span className={`text-xs font-medium text-center ${unlocked ? 'text-white' : 'text-gray-500'}`}>
        {badge.name}
      </span>
    </motion.div>
  );
}

/**
 * Composant Stats personnelles fun
 */
export function PersonalStats({ customer, cards = [], isDarkMode = true }) {
  const stats = calculateStats(customer, cards);
  
  // Calculer le temps passé sur l'app (estimation basée sur les visites)
  const totalConnections = customer?.totalConnections || customer?.loginStreak || 1;
  const avgTimePerVisit = 8; // minutes estimées par visite
  const totalTimeMinutes = totalConnections * avgTimePerVisit;
  const totalTimeHours = Math.floor(totalTimeMinutes / 60);
  const remainingMinutes = totalTimeMinutes % 60;
  const timeDisplay = totalTimeHours > 0 ? `${totalTimeHours}h${remainingMinutes}` : `${totalTimeMinutes}min`;
  
  // Dernière connexion
  const lastLogin = customer?.lastLogin || customer?.updatedAt || new Date().toISOString();
  const lastLoginDate = new Date(lastLogin);
  const now = new Date();
  const diffHours = Math.floor((now - lastLoginDate) / (1000 * 60 * 60));
  const lastLoginDisplay = diffHours < 1 ? 'À l\'instant' : 
    diffHours < 24 ? `Il y a ${diffHours}h` : 
    `Il y a ${Math.floor(diffHours / 24)}j`;
  
  const statItems = [
    {
      icon: Calendar,
      label: 'Avec nous depuis',
      value: stats.daysSinceJoin,
      suffix: 'jours',
      color: '#a855f7',
      emoji: stats.daysSinceJoin > 180 ? '🏆' : stats.daysSinceJoin > 30 ? '⭐' : '🌱'
    },
    {
      icon: Star,
      label: 'Tampons collectés',
      value: stats.totalStamps,
      suffix: '',
      color: '#fbbf24',
      emoji: stats.totalStamps > 50 ? '🔥' : stats.totalStamps > 10 ? '✨' : '💫'
    },
    {
      icon: Gift,
      label: 'Économies réalisées',
      value: stats.totalSavings,
      suffix: '€',
      color: '#22c55e',
      emoji: stats.totalSavings > 50 ? '💰' : stats.totalSavings > 20 ? '💵' : '🎁'
    },
    {
      icon: Users,
      label: 'Amis parrainés',
      value: stats.referralCount,
      suffix: '',
      color: '#06b6d4',
      emoji: stats.referralCount > 5 ? '👑' : stats.referralCount > 0 ? '🤝' : '👋'
    },
    // 🆕 NOUVELLES STATS DE CONNEXION
    {
      icon: Zap,
      label: 'Connexions',
      value: totalConnections,
      suffix: '',
      color: '#f97316',
      emoji: totalConnections > 50 ? '⚡' : totalConnections > 20 ? '🔥' : '✨'
    },
    {
      icon: Clock,
      label: 'Temps sur l\'app',
      value: timeDisplay,
      suffix: '',
      color: '#ec4899',
      emoji: totalTimeHours > 10 ? '🚀' : totalTimeHours > 5 ? '💜' : '⏱️'
    },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        {statItems.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="rounded-xl p-4 text-center"
            style={{
              background: isDarkMode 
                ? `linear-gradient(135deg, ${stat.color}20 0%, ${stat.color}10 100%)`
                : `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}05 100%)`,
              border: `1px solid ${stat.color}30`
            }}
          >
            <div className="text-2xl mb-1">{stat.emoji}</div>
            <div 
              className="text-2xl font-black"
              style={{ color: stat.color }}
            >
              {stat.value}{stat.suffix}
            </div>
            <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {stat.label}
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* 🆕 Dernière connexion */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="rounded-xl p-3 text-center"
        style={{
          background: isDarkMode 
            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.05) 100%)',
          border: '1px solid rgba(99, 102, 241, 0.3)'
        }}
      >
        <div className="flex items-center justify-center gap-2">
          <span className="text-lg">🛸</span>
          <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Dernière visite : <strong className="text-indigo-400">{lastLoginDisplay}</strong>
          </span>
        </div>
      </motion.div>
    </div>
  );
}

/**
 * Composant grille de badges
 */
export function BadgesGrid({ customer, cards = [], isDarkMode = true }) {
  const unlockedBadges = calculateBadges(customer, cards);
  const allBadges = Object.values(BADGES);
  
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          🏅 Tes badges
        </h3>
        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          {unlockedBadges.length}/{allBadges.length} débloqués
        </span>
      </div>
      
      <div className="grid grid-cols-5 sm:grid-cols-6 gap-3">
        {allBadges.map((badge) => (
          <Badge 
            key={badge.id} 
            badge={badge} 
            unlocked={unlockedBadges.some(b => b.id === badge.id)}
            size="sm"
          />
        ))}
      </div>
    </div>
  );
}

export default { Badge, PersonalStats, BadgesGrid, calculateBadges, calculateStats, BADGES };
